/**
 * useHowieDojoWebSocket — WebSocket hook for HOWie Dojo practice mode.
 *
 * Mirrors useHowieCorridorWebSocket but adapted for the dojo unit round model:
 *  - Beats (intake → highlight → react_well → react_better → enact → chiddush)
 *  - Profile-driven (15-cell awareness map, learner experience level)
 *  - Unit-based rounds; the server drives most beat transitions, the client closes
 *    highlight→react_well with `submit_marks` (the learner's own marks) and
 *    chiddush→completed via `advance_beat`
 *  - Insight save at round end
 *
 * Backend tools called: get_user_dojo_profile, get_dojo_units, get_kg_context,
 * log_round_signal, save_insight, get_session_coaching_summary, update_dialectic_position
 * (see railway-howie-service/tools/howie_dojo_tools.py).
 *
 * WebSocket protocol: mode='dojo' on the same /ws/howie/<session> endpoint as corridor.
 * Wire contract: DOCS/howie/dojo-units-v1/CONTRACT.md.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase, getValidAccessToken, handleInvalidSession } from '@/services/supabase';
import {
  DojoBeat,
  DojoExperienceLevel,
  DojoImperativeProgress,
  DojoIntakeAnswers,
  DojoJudgeSummary,
  DojoLearnerMark,
  DojoMessage,
  DojoProfile,
  DojoRoundCompletion,
  DojoScenario,
  DojoUnit,
  DojoUnitsCatalog,
  DojoPracticeProgress,
  DialecticPosition,
  GCLDimension,
  FifteenCellMap,
  UseHowieDojoWebSocketProps,
  UseHowieDojoWebSocketReturn,
} from '@/types/dojo';
import {
  isClientDrivenTransition,
  isValidBeatTransition,
  normalizeDojoBeat,
} from '@/lib/dojoBeats';
import { getHowieApiUrl, getHowieWsBaseUrl } from '@/config/howieService';
import { useResumableChat } from '@lightning/hooks';
import {
  clearPendingChatCommand,
  loadPendingChatCommand,
  savePendingChatCommand,
  type ResumableTurnSnapshot,
} from '@lightning/utils';

const MAX_MESSAGES_IN_MEMORY = 100;
const WS_CONNECT_TIMEOUT_MS = 10000;

// Re-exported so callers and tests reach the beat machine through the hook module
// as well as through @/lib/dojoBeats.
export { isValidBeatTransition, normalizeDojoBeat } from '@/lib/dojoBeats';

const DEFAULT_FIFTEEN_CELL_MAP: FifteenCellMap = {
  KNOW: { level: 'comply', confidence: 0, last_engaged: null },
  BEHAVE: { level: 'comply', confidence: 0, last_engaged: null },
  RELATE: { level: 'comply', confidence: 0, last_engaged: null },
  RECOGNIZE: { level: 'comply', confidence: 0, last_engaged: null },
  PURSUE: { level: 'comply', confidence: 0, last_engaged: null },
};

const DEFAULT_PROFILE: DojoProfile = {
  fifteen_cell_map: DEFAULT_FIFTEEN_CELL_MAP,
  dialectic_positions: {},
  commitment_cadence: null,
  preferred_voice: null,
  rounds_completed: 0,
  is_first_session: true,
  last_chiddush_id: null,
  experience_level: null,
};

const EXPERIENCE_LEVELS: DojoExperienceLevel[] = ['new_manager', 'mid_level', 'senior_executive'];

function normalizeExperienceLevel(value: unknown): DojoExperienceLevel | null {
  return EXPERIENCE_LEVELS.includes(value as DojoExperienceLevel)
    ? (value as DojoExperienceLevel)
    : null;
}

// The profile arrives from three places (Supabase row, REST catalog, websocket);
// experience_level is the one field older payloads may omit entirely.
function withExperienceLevel(profile: DojoProfile): DojoProfile {
  return {
    ...profile,
    experience_level: normalizeExperienceLevel(profile?.experience_level),
  };
}

// WebSocket message contracts shared with backend.
interface WSAuthMessage {
  type: 'auth';
  token: string;
}

interface WSDojoChatMessage {
  type: 'message';
  content: string;
  mode: 'dojo';
  round_id?: string;
  turn_id?: string;
  client_message_id?: string;
}

interface WSDojoSteerMessage {
  type: 'steer_turn';
  turn_id: string;
  content: string;
  next_turn_id: string;
  client_message_id: string;
  mode: 'dojo';
  round_id?: string;
}

interface WSStartRoundMessage {
  type: 'start_round';
  mode: 'dojo';
  target_dimension?: GCLDimension;
  scenario_id?: string;
}

interface WSSaveInsightMessage {
  type: 'save_chiddush';
  round_id: string;
  text: string;
  share_permission: boolean;
}

interface WSEndRoundEarlyMessage {
  type: 'end_round_early';
  round_id: string;
}

interface WSSubmitIntakeMessage {
  type: 'submit_intake';
  round_id: string;
  answers: DojoIntakeAnswers;
}

interface WSSubmitMarksMessage {
  type: 'submit_marks';
  round_id: string;
  marks: DojoLearnerMark[];
}

interface WSAdvanceBeatMessage {
  type: 'advance_beat';
  round_id: string;
  to_beat: DojoBeat;
}

interface WSSetExperienceLevelMessage {
  type: 'set_experience_level';
  level: DojoExperienceLevel;
}

interface WSChunkMessage {
  type: 'chunk';
  content: string;
}

interface WSCompleteMessage {
  type: 'complete';
  content: string;
  round_id?: string;
}

interface WSErrorMessage {
  type: 'error';
  content: string;
  error_code?: string;
}

interface WSDojoProfileMessage {
  type: 'dojo_profile';
  profile: DojoProfile;
}

interface WSDojoScenarioMessage {
  type: 'dojo_scenario';
  round_id: string;
  scenario: DojoScenario;
}

interface WSDojoUnitMessage {
  type: 'dojo_unit';
  round_id: string;
  unit: DojoUnit;
  // Present on reconnect so a resumed round lands back on its beat.
  beat?: DojoBeat | string;
  // Present on reconnect: the marks the learner already made.
  marks?: unknown;
}

// Marks come back off the round as jsonb. They were validated on the way in, but
// a shape check here keeps a bad row from corrupting the offsets the panel
// renders from.
function normalizeLearnerMarks(value: unknown): DojoLearnerMark[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const mark = entry as Record<string, unknown>;
    const { id, paragraph, start, end, quote, kind } = mark;
    if (typeof id !== 'string' || typeof quote !== 'string') return [];
    if (typeof paragraph !== 'number' || typeof start !== 'number' || typeof end !== 'number') {
      return [];
    }
    if (kind !== 'admirable' && kind !== 'adequate') return [];
    if (end <= start) return [];
    return [{ id, paragraph, start, end, quote, kind }];
  });
}

interface WSDojoBeatTransitionMessage {
  type: 'dojo_beat_transition';
  round_id: string;
  from_beat: DojoBeat;
  to_beat: DojoBeat;
}

interface WSDojoRoundCompleteMessage {
  type: 'dojo_round_complete';
  round_id: string;
  chiddush_id: string | null;
  updated_profile: DojoProfile;
  imperative_progress?: DojoImperativeProgress | null;
  judge_summary?: DojoJudgeSummary | null;
  beat?: DojoBeat | string;
}

type WSOutgoing =
  | WSAuthMessage
  | WSDojoChatMessage
  | WSDojoSteerMessage
  | WSStartRoundMessage
  | WSSaveInsightMessage
  | WSEndRoundEarlyMessage
  | WSSubmitIntakeMessage
  | WSSubmitMarksMessage
  | WSAdvanceBeatMessage
  | WSSetExperienceLevelMessage;

interface UnitsCatalogResponse {
  success?: boolean;
  profile?: DojoProfile;
  imperatives?: DojoUnitsCatalog['imperatives'];
  units?: DojoUnitsCatalog['units'];
  recommended_unit_id?: string | null;
  recommended_reason?: string | null;
  progress?: DojoPracticeProgress;
  mastery?: DojoUnitsCatalog['mastery'];
  rounds_completed?: number;
  error?: string;
}

function generateSessionId(): string {
  return `howie-dojo-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function useHowieDojoWebSocket(
  props: UseHowieDojoWebSocketProps
): UseHowieDojoWebSocketReturn {
  const { userId, onBeatChange, onRoundComplete, onProfileUpdate } = props;

  // Messages + streaming
  const [messages, setMessages] = useState<DojoMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');

  // Dojo state
  const [currentBeat, setCurrentBeat] = useState<DojoBeat>('idle');
  const [currentRoundId, setCurrentRoundId] = useState<string | null>(null);
  const [currentUnit, setCurrentUnit] = useState<DojoUnit | null>(null);
  const [learnerMarks, setLearnerMarks] = useState<DojoLearnerMark[]>([]);
  const [currentScenario, setCurrentScenario] = useState<DojoScenario | null>(null);
  const [catalog, setCatalog] = useState<DojoUnitsCatalog | null>(null);
  const [practiceProgress, setPracticeProgress] = useState<DojoPracticeProgress | null>(null);
  const [profile, setProfile] = useState<DojoProfile | null>(null);
  const [lastCompletion, setLastCompletion] = useState<DojoRoundCompletion | null>(null);

  // Status
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [canSteer, setCanSteer] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'error'
  >('disconnected');
  const resumableSurface = `dojo:${userId || 'anonymous'}`;
  const {
    activeTurn,
    beginTurn,
    updateTurn,
    acceptSnapshot,
    finishTurn,
    draft,
    setDraft,
    clearDraft,
  } = useResumableChat({
    app: 'howie',
    surface: resumableSurface,
    conversationId: currentRoundId,
    migrateDraftOnConversationChange: false,
  });

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string>(generateSessionId());
  const isAuthenticatedRef = useRef(false);
  const lastMessageRef = useRef<string | null>(null);
  const messageQueueRef = useRef<WSOutgoing[]>([]);
  const streamingTextRef = useRef('');
  const intentionalDisconnectRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const currentBeatRef = useRef<DojoBeat>('idle');
  const currentRoundIdRef = useRef<string | null>(null);
  const connectRef = useRef<(() => Promise<void>) | null>(null);
  const activeTurnRef = useRef(activeTurn);
  useEffect(() => { activeTurnRef.current = activeTurn; }, [activeTurn]);

  const updateActiveTurn = useCallback((updates: Parameters<typeof updateTurn>[0]) => {
    if (activeTurnRef.current) {
      activeTurnRef.current = {
        ...activeTurnRef.current,
        ...updates,
        updatedAt: new Date().toISOString(),
      };
    }
    updateTurn(updates);
  }, [updateTurn]);

  const finishActiveTurn = useCallback(() => {
    activeTurnRef.current = null;
    finishTurn();
  }, [finishTurn]);

  // Stream events own the buffer. Copying rendered state back into it can
  // overwrite newer chunks that arrived before an effect ran.
  // Keep the remaining state refs synced.
  useEffect(() => {
    currentBeatRef.current = currentBeat;
  }, [currentBeat]);
  useEffect(() => {
    currentRoundIdRef.current = currentRoundId;
  }, [currentRoundId]);

  // ---------- Internal send (assumes authenticated) ----------

  const sendRaw = useCallback((msg: WSOutgoing) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      messageQueueRef.current.push(msg);
      return;
    }
    if (!isAuthenticatedRef.current) {
      messageQueueRef.current.push(msg);
      return;
    }
    wsRef.current.send(JSON.stringify(msg));
  }, []);

  const loadUnitsCatalog = useCallback(async (): Promise<void> => {
    if (!userId) {
      setCatalog(null);
      return;
    }

    setIsLoadingCatalog(true);
    setCatalogError(null);
    try {
      const accessToken = await getValidAccessToken();
      if (!accessToken) {
        setCatalog(null);
        setCatalogError('Sign in again to load your units.');
        return;
      }

      const response = await fetch(`${getHowieApiUrl()}/api/dojo/units`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Units request failed (${response.status})`);
      }

      const payload = (await response.json()) as UnitsCatalogResponse;
      if (payload.error) {
        throw new Error(payload.error);
      }

      const nextProfile = payload.profile ? withExperienceLevel(payload.profile) : null;
      if (nextProfile) {
        setProfile(nextProfile);
        if (onProfileUpdate) onProfileUpdate(nextProfile);
      }

      setCatalog({
        profile: nextProfile,
        imperatives: payload.imperatives ?? [],
        units: payload.units ?? [],
        recommended_unit_id: payload.recommended_unit_id ?? null,
        recommended_reason: payload.recommended_reason ?? null,
        progress: payload.progress ?? null,
        mastery: payload.mastery ?? null,
        rounds_completed:
          payload.rounds_completed ?? payload.progress?.rounds_completed ?? nextProfile?.rounds_completed ?? 0,
      });
      setPracticeProgress(payload.progress ?? null);
    } catch (err) {
      console.error('[Dojo WS] Units catalog load failed:', err);
      setCatalog(null);
      setCatalogError(err instanceof Error ? err.message : 'Your units could not load.');
    } finally {
      setIsLoadingCatalog(false);
    }
  }, [userId, onProfileUpdate]);

  // ---------- Connect ----------

  const connect = useCallback(async () => {
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    const baseUrl = getHowieWsBaseUrl();
    const wsUrl = `${baseUrl}/ws/howie/${sessionIdRef.current}`;
    setConnectionStatus('connecting');
    intentionalDisconnectRef.current = false;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      const connectTimeout = window.setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.warn('[Dojo WS] Connection timed out; reconnecting.');
          ws.close();
        }
      }, WS_CONNECT_TIMEOUT_MS);

      ws.onopen = async () => {
        window.clearTimeout(connectTimeout);
        wsRef.current = ws;
        const accessToken = await getValidAccessToken();
        if (!accessToken) {
          setError('Session expired. Please log in again.');
          setHasError(true);
          setConnectionStatus('error');
          setIsLoading(false);
          ws.close();
          return;
        }
        ws.send(JSON.stringify({ type: 'auth', token: accessToken }));
      };

      ws.onmessage = (event) => {
        // WebSocket payloads are discriminated dynamically and include
        // several mode-specific result shapes.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch (parseError) {
          console.error('[Dojo WS] Parse failed:', parseError);
          return;
        }

        switch (data.type) {
          case 'auth_success': {
            setConnectionStatus('connected');
            isAuthenticatedRef.current = true;
            reconnectAttemptRef.current = 0;
            setIsLoading(false);
            const pending = loadPendingChatCommand<Record<string, unknown>>('howie', resumableSurface);
            if (pending) ws.send(JSON.stringify(pending));
            if (activeTurnRef.current) {
              ws.send(JSON.stringify({
                type: 'sync_turn',
                turn_id: activeTurnRef.current.turnId,
                after_sequence: activeTurnRef.current.lastSequence,
              }));
            }
            // Flush queued messages
            if (messageQueueRef.current.length > 0) {
              messageQueueRef.current.forEach((msg) => sendRaw(msg));
              messageQueueRef.current = [];
            }
            break;
          }

          case 'message_ack':
            {
              const pending = loadPendingChatCommand<Record<string, unknown>>('howie', resumableSurface);
              const pendingTurnId = pending?.type === 'steer_turn' ? pending.next_turn_id : pending?.turn_id;
              if (!pendingTurnId || !data.turn_id || pendingTurnId === data.turn_id) {
                clearPendingChatCommand('howie', resumableSurface);
              }
            }
            if (data.turn_id && data.turn_id !== activeTurnRef.current?.turnId) break;
            setCanSteer(data.capabilities?.steer === true);
            if (data.conversation_id && !currentRoundIdRef.current) {
              currentRoundIdRef.current = data.conversation_id;
              setCurrentRoundId(data.conversation_id);
            }
            updateActiveTurn({
              status: 'running',
              conversationId: data.conversation_id || activeTurnRef.current?.conversationId || null,
              lastSequence: Math.max(activeTurnRef.current?.lastSequence || 0, Number(data.last_sequence ?? data.sequence) || 0),
            });
            break;

          case 'turn_snapshot': {
            const snapshot = data as ResumableTurnSnapshot & { round_id?: string };
            if (snapshot.turn_id !== activeTurnRef.current?.turnId) break;
            const current = activeTurnRef.current;
            activeTurnRef.current = current ? {
              ...current,
              conversationId: snapshot.conversation_id || current.conversationId,
              lastSequence: Math.max(current.lastSequence, snapshot.last_sequence || 0),
              status: snapshot.status,
              updatedAt: new Date().toISOString(),
            } : current;
            acceptSnapshot(snapshot);
            const restoredRoundId = snapshot.round_id || snapshot.conversation_id;
            if (restoredRoundId && !currentRoundIdRef.current) {
              currentRoundIdRef.current = restoredRoundId;
              setCurrentRoundId(restoredRoundId);
            }
            const userContent = snapshot.user_message?.content?.trim();
            if (userContent) {
              setMessages((prev) => prev.some((message) => message.id === snapshot.client_message_id)
                ? prev
                : [...prev, {
                    id: snapshot.client_message_id,
                    role: 'user' as const,
                    content: userContent,
                    timestamp: snapshot.user_message.timestamp || new Date().toISOString(),
                    beat: currentBeatRef.current,
                    round_id: restoredRoundId || undefined,
                  }].slice(-MAX_MESSAGES_IN_MEMORY));
            }
            streamingTextRef.current = snapshot.assistant_content || '';
            setStreamingText(streamingTextRef.current);
            if (snapshot.status === 'completed') {
              const id = `${snapshot.turn_id}:assistant`;
              if (snapshot.assistant_content.trim()) {
                setMessages((prev) => prev.some((message) => message.id === id)
                  ? prev
                  : [...prev, {
                      id,
                      role: 'assistant' as const,
                      content: snapshot.assistant_content,
                      timestamp: new Date().toISOString(),
                      beat: currentBeatRef.current,
                      round_id: restoredRoundId || undefined,
                    }].slice(-MAX_MESSAGES_IN_MEMORY));
              }
              streamingTextRef.current = '';
              setStreamingText('');
              setIsSending(false);
              setIsStreaming(false);
              clearPendingChatCommand('howie', resumableSurface);
              finishActiveTurn();
            } else if (snapshot.status === 'stopped' || snapshot.status === 'interrupted' || snapshot.status === 'failed') {
              setIsSending(false);
              setIsStreaming(false);
              setHasError(snapshot.status === 'failed');
              setError(snapshot.error || null);
              clearPendingChatCommand('howie', resumableSurface);
              finishActiveTurn();
            } else {
              setIsSending(true);
              setIsStreaming(Boolean(snapshot.assistant_content));
            }
            break;
          }

          case 'auth_failed':
            setError('Authentication failed');
            setHasError(true);
            setConnectionStatus('error');
            setIsLoading(false);
            ws.close();
            handleInvalidSession()
              .then((refreshed) => {
                if (refreshed) {
                  setHasError(false);
                  setError(null);
                  sessionIdRef.current = generateSessionId();
                  void connectRef.current?.();
                } else {
                  setError('Session expired. Sign in again to continue practice.');
                  setHasError(true);
                }
              })
              .catch((err) => {
                console.error('[Dojo WS] Session refresh failed:', err);
                setError('Session refresh failed. Sign in again to continue practice.');
                setHasError(true);
              });
            break;

          case 'chunk': {
            if (data.turn_id && data.turn_id !== activeTurnRef.current?.turnId) break;
            const sequence = Number(data.sequence) || 0;
            if (sequence && sequence <= (activeTurnRef.current?.lastSequence || 0)) break;
            if (sequence) updateActiveTurn({ lastSequence: sequence, status: 'running' });
            const nextStreamingText = streamingTextRef.current + (data as WSChunkMessage).content;
            streamingTextRef.current = nextStreamingText;
            setIsStreaming(true);
            setStreamingText(nextStreamingText);
            break;
          }

          case 'chunk_reset':
            streamingTextRef.current = '';
            setStreamingText('');
            break;

          case 'complete':
          case 'turn_complete': {
            setIsStreaming(false);
            const completeData = data as WSCompleteMessage & { turn_id?: string; assistant_content?: string };
            if (completeData.turn_id && completeData.turn_id !== activeTurnRef.current?.turnId) break;
            // The server’s completed text repairs any missing live chunks.
            const finalContent = completeData.assistant_content ?? completeData.content ?? streamingTextRef.current;

            const assistantMessage: DojoMessage = {
              id: completeData.turn_id ? `${completeData.turn_id}:assistant` : undefined,
              role: 'assistant',
              content: finalContent,
              timestamp: new Date().toISOString(),
              beat: currentBeatRef.current,
              round_id: completeData.round_id ?? currentRoundIdRef.current ?? undefined,
            };
            setMessages((prev) => assistantMessage.id && prev.some((message) => message.id === assistantMessage.id)
              ? prev
              : [...prev, assistantMessage].slice(-MAX_MESSAGES_IN_MEMORY));
            streamingTextRef.current = '';
            setStreamingText('');
            if (completeData.round_id && !currentRoundIdRef.current) {
              setCurrentRoundId(completeData.round_id);
            }
            setIsSending(false);
            clearPendingChatCommand('howie', resumableSurface);
            finishActiveTurn();
            break;
          }

          case 'turn_stopped': {
            if (data.turn_id && data.turn_id !== activeTurnRef.current?.turnId) break;
            const partial = String(data.assistant_content ?? data.content ?? streamingTextRef.current ?? '');
            if (partial.trim()) {
              const id = `${data.turn_id || activeTurnRef.current?.turnId}:assistant`;
              setMessages((prev) => prev.some((message) => message.id === id)
                ? prev
                : [...prev, {
                    id,
                    role: 'assistant' as const,
                    content: partial,
                    timestamp: new Date().toISOString(),
                    beat: currentBeatRef.current,
                    round_id: currentRoundIdRef.current || undefined,
                  }].slice(-MAX_MESSAGES_IN_MEMORY));
            }
            streamingTextRef.current = '';
            setStreamingText('');
            setIsSending(false);
            setIsStreaming(false);
            clearPendingChatCommand('howie', resumableSurface);
            finishActiveTurn();
            break;
          }

          case 'error': {
            const errorData = data as WSErrorMessage;
            console.error('[Dojo WS] Error:', errorData.content);
            setError(errorData.content);
            setHasError(true);
            setIsSending(false);
            setIsStreaming(false);
            streamingTextRef.current = '';
            setStreamingText('');
            clearPendingChatCommand('howie', resumableSurface);
            finishActiveTurn();
            break;
          }

          case 'tool_use':
            // Silent — HOWie's tool calls are invisible to the user per the prompt rules.
            break;

          case 'dojo_profile': {
            const profileData = data as WSDojoProfileMessage;
            const nextProfile = withExperienceLevel(profileData.profile);
            setProfile(nextProfile);
            if (onProfileUpdate) onProfileUpdate(nextProfile);
            break;
          }

          case 'dojo_unit': {
            const unitData = data as WSDojoUnitMessage;
            setCurrentRoundId(unitData.round_id);
            currentRoundIdRef.current = unitData.round_id;
            setCurrentUnit(unitData.unit);
            setLearnerMarks(normalizeLearnerMarks(unitData.marks));
            setCurrentScenario(null);
            // Re-sent on reconnect: honor the beat the server says the round is on
            // so a refresh mid-round rehydrates the panel (and marks, once made).
            const resumedBeat = normalizeDojoBeat(unitData.beat) ?? 'intake';
            currentBeatRef.current = resumedBeat;
            setCurrentBeat(resumedBeat);
            setIsSending(false);
            if (onBeatChange) onBeatChange(resumedBeat);
            break;
          }

          case 'dojo_scenario': {
            // Legacy scenario rounds still in flight. The unit surface cannot render
            // them, but keeping the case means the round id and beat stay coherent.
            const scenarioData = data as WSDojoScenarioMessage;
            setCurrentRoundId(scenarioData.round_id);
            currentRoundIdRef.current = scenarioData.round_id;
            setCurrentScenario(scenarioData.scenario);
            setCurrentUnit(null);
            currentBeatRef.current = 'intake';
            setCurrentBeat('intake');
            setIsSending(false);
            if (onBeatChange) onBeatChange('intake');
            break;
          }

          case 'dojo_transcript': {
            // Resume replay: the server re-sends the round's prior turns after a
            // reconnect so a mid-round refresh restores the conversation.
            const replay = data as { round_id?: string; messages?: unknown[] };
            const restored: DojoMessage[] = (replay.messages ?? [])
              .filter(
                (m): m is Record<string, unknown> =>
                  typeof m === 'object' && m !== null &&
                  ((m as Record<string, unknown>).role === 'user' ||
                    (m as Record<string, unknown>).role === 'assistant') &&
                  typeof (m as Record<string, unknown>).content === 'string'
              )
              .map((m, i) => ({
                id: `replay-${i}`,
                role: m.role as 'user' | 'assistant',
                content: m.content as string,
                timestamp: typeof m.timestamp === 'string' ? m.timestamp : new Date(0).toISOString(),
                beat: normalizeDojoBeat(m.beat as string | undefined) ?? undefined,
                round_id: replay.round_id,
              }));
            if (restored.length > 0) setMessages(restored);
            break;
          }

          case 'dojo_beat_transition': {
            const transition = data as WSDojoBeatTransitionMessage;
            const fromBeat = normalizeDojoBeat(transition.from_beat);
            const toBeat = normalizeDojoBeat(transition.to_beat);
            if (!fromBeat || !toBeat || !isValidBeatTransition(fromBeat, toBeat)) {
              console.warn(
                '[Dojo WS] Ignoring invalid beat transition:',
                transition.from_beat,
                '->',
                transition.to_beat
              );
              break;
            }
            currentBeatRef.current = toBeat;
            setCurrentBeat(toBeat);
            // Model-free client actions (submit_intake, advance_beat) are answered
            // only by this transition — it doubles as their ack, so the composer
            // must unlock here or those beats wedge the round.
            setIsSending(false);
            if (onBeatChange) onBeatChange(toBeat);
            break;
          }

          case 'dojo_round_complete': {
            const completed = data as WSDojoRoundCompleteMessage;
            // Skipping the insight completes the round without a chiddush id, so the
            // absence of one only means "abandoned" when the round ended before then.
            const terminalBeat: DojoBeat =
              normalizeDojoBeat(completed.beat) ??
              (completed.chiddush_id ||
              currentBeatRef.current === 'chiddush' ||
              currentBeatRef.current === 'completed'
                ? 'completed'
                : 'abandoned');
            currentBeatRef.current = terminalBeat;
            setCurrentBeat(terminalBeat);
            const nextProfile = withExperienceLevel(completed.updated_profile);
            setProfile(nextProfile);
            setLastCompletion({
              roundId: completed.round_id,
              insightId: completed.chiddush_id,
              imperativeProgress: completed.imperative_progress ?? null,
              judgeSummary: completed.judge_summary ?? null,
            });
            setIsSending(false);
            setIsStreaming(false);
            streamingTextRef.current = '';
            setStreamingText('');
            if (onBeatChange) onBeatChange(terminalBeat);
            if (onProfileUpdate) onProfileUpdate(nextProfile);
            if (onRoundComplete) onRoundComplete(completed.round_id, completed.chiddush_id);
            void loadUnitsCatalog();
            break;
          }

          default:
            console.warn('[Dojo WS] Unknown type:', (data as { type?: string }).type);
        }
      };

      ws.onerror = (event) => {
        if (wsRef.current !== ws) return;
        window.clearTimeout(connectTimeout);
        console.error('[Dojo WS] Error event:', event);
        setError('WebSocket connection error');
        setHasError(true);
        setConnectionStatus('error');
        setIsLoading(false);
        setIsSending(false);
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        window.clearTimeout(connectTimeout);
        setConnectionStatus('disconnected');
        setIsSending(false);
        isAuthenticatedRef.current = false;
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        if (!intentionalDisconnectRef.current) {
          const backoffMs = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 16000);
          reconnectAttemptRef.current += 1;
          setTimeout(() => {
            if (!intentionalDisconnectRef.current) void connectRef.current?.();
          }, backoffMs);
        } else {
          intentionalDisconnectRef.current = false;
        }
      };
    } catch (err) {
      console.error('[Dojo WS] Connect failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setHasError(true);
      setConnectionStatus('error');
      setIsLoading(false);
    }
  }, [acceptSnapshot, finishActiveTurn, loadUnitsCatalog, onBeatChange, onProfileUpdate, onRoundComplete, resumableSurface, sendRaw, updateActiveTurn]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback((intentional: boolean = false) => {
    if (intentional) intentionalDisconnectRef.current = true;
    messageQueueRef.current = [];
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      isAuthenticatedRef.current = false;
    }
  }, []);

  // ---------- Public actions ----------

  const startRound = useCallback(
    async (targetDimension?: GCLDimension, scenarioId?: string): Promise<void> => {
      if (!userId) {
        setError('User not authenticated');
        setHasError(true);
        return;
      }
      setIsSending(true);
      setHasError(false);
      setError(null);
      setMessages([]);
      streamingTextRef.current = '';
      setStreamingText('');
      setCurrentUnit(null);
      setLearnerMarks([]);
      setCurrentScenario(null);
      setLastCompletion(null);
      setCurrentRoundId(null);
      currentRoundIdRef.current = null;

      const msg: WSStartRoundMessage = {
        type: 'start_round',
        mode: 'dojo',
        target_dimension: targetDimension,
        scenario_id: scenarioId,
      };

      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await connect();
        messageQueueRef.current.push(msg);
      } else {
        sendRaw(msg);
      }
    },
    [userId, connect, sendRaw]
  );

  const sendMessage = useCallback(
    async (message: string): Promise<void> => {
      if (!userId) {
        setError('User not authenticated');
        setHasError(true);
        return;
      }
      if (!message.trim()) return;

      const active = activeTurnRef.current;
      if (isSending && (!canSteer || active?.status === 'queued')) {
        return;
      }

      if (isSending && active) {
        const nextTurn = beginTurn(currentRoundIdRef.current);
        activeTurnRef.current = nextTurn;
        const msg: WSDojoSteerMessage = {
          type: 'steer_turn',
          turn_id: active.turnId,
          content: message.trim(),
          next_turn_id: nextTurn.turnId,
          client_message_id: nextTurn.clientMessageId,
          mode: 'dojo',
          round_id: currentRoundIdRef.current ?? undefined,
        };
        const partial = streamingTextRef.current;
        setMessages((prev) => {
          const withPartial = partial.trim() && !prev.some((item) => item.id === `${active.turnId}:assistant`)
            ? [...prev, {
                id: `${active.turnId}:assistant`,
                role: 'assistant' as const,
                content: partial,
                timestamp: new Date().toISOString(),
                beat: currentBeatRef.current,
                round_id: currentRoundIdRef.current ?? undefined,
              }]
            : prev;
          return [...withPartial, {
            id: nextTurn.clientMessageId,
            role: 'user' as const,
            content: message.trim(),
            timestamp: new Date().toISOString(),
            beat: currentBeatRef.current,
            round_id: currentRoundIdRef.current ?? undefined,
          }].slice(-MAX_MESSAGES_IN_MEMORY);
        });
        streamingTextRef.current = '';
        setStreamingText('');
        setIsStreaming(false);
        lastMessageRef.current = message.trim();
        savePendingChatCommand('howie', resumableSurface, msg as unknown as Record<string, unknown>);
        clearDraft();
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          await connect();
        } else if (isAuthenticatedRef.current) {
          sendRaw(msg);
        }
        return;
      }

      setIsSending(true);
      setHasError(false);
      setError(null);
      setStreamingText('');
      streamingTextRef.current = '';

      const resumableTurn = activeTurnRef.current?.status === 'queued'
        ? activeTurnRef.current
        : beginTurn(currentRoundIdRef.current);
      activeTurnRef.current = resumableTurn;

      const userMessage: DojoMessage = {
        id: resumableTurn.clientMessageId,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        beat: currentBeatRef.current,
        round_id: currentRoundIdRef.current ?? undefined,
      };
      setMessages((prev) => [...prev, userMessage].slice(-MAX_MESSAGES_IN_MEMORY));
      lastMessageRef.current = message;

      const msg: WSDojoChatMessage = {
        type: 'message',
        content: message,
        mode: 'dojo',
        round_id: currentRoundIdRef.current ?? undefined,
        turn_id: resumableTurn.turnId,
        client_message_id: resumableTurn.clientMessageId,
      };
      savePendingChatCommand('howie', resumableSurface, msg as unknown as Record<string, unknown>);
      clearDraft();

      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await connect();
      } else if (!isAuthenticatedRef.current) {
        // The durable outbox is flushed immediately after authentication.
      } else {
        sendRaw(msg);
      }
    },
    [beginTurn, canSteer, clearDraft, connect, isSending, resumableSurface, sendRaw, userId]
  );

  const saveInsight = useCallback(
    async (text: string, sharePermission: boolean = false): Promise<void> => {
      if (!currentRoundIdRef.current) {
        console.warn('[Dojo WS] saveInsight called with no active round');
        return;
      }
      setIsSending(true);
      setHasError(false);
      setError(null);
      const msg: WSSaveInsightMessage = {
        type: 'save_chiddush',
        round_id: currentRoundIdRef.current,
        text,
        share_permission: sharePermission,
      };
      sendRaw(msg);
    },
    [sendRaw]
  );

  const endRoundEarly = useCallback(async (): Promise<void> => {
    if (!currentRoundIdRef.current) return;
    setIsSending(true);
    setHasError(false);
    setError(null);
    const msg: WSEndRoundEarlyMessage = {
      type: 'end_round_early',
      round_id: currentRoundIdRef.current,
    };
    sendRaw(msg);
  }, [sendRaw]);

  // Both intake answers travel together in a single message; the client renders the
  // two questions itself, so no model round-trip happens until this fires.
  const submitIntake = useCallback(
    async (answers: DojoIntakeAnswers): Promise<void> => {
      if (!currentRoundIdRef.current) {
        console.warn('[Dojo WS] submitIntake called with no active round');
        return;
      }
      setIsSending(true);
      setHasError(false);
      setError(null);
      const msg: WSSubmitIntakeMessage = {
        type: 'submit_intake',
        round_id: currentRoundIdRef.current,
        answers,
      };
      sendRaw(msg);
    },
    [sendRaw]
  );

  // Save the learner’s highlights and open the discussion of their interpretation.
  const submitMarks = useCallback(
    async (marks: DojoLearnerMark[]): Promise<void> => {
      if (!currentRoundIdRef.current) {
        console.warn('[Dojo WS] submitMarks called with no active round');
        return;
      }
      setIsSending(true);
      setHasError(false);
      setError(null);
      const msg: WSSubmitMarksMessage = {
        type: 'submit_marks',
        round_id: currentRoundIdRef.current,
        marks,
      };
      sendRaw(msg);
    },
    [sendRaw]
  );

  // Validate learner-owned transitions locally; the server checks stored responses.
  const advanceBeat = useCallback(
    async (toBeat: DojoBeat): Promise<void> => {
      if (!currentRoundIdRef.current) {
        console.warn('[Dojo WS] advanceBeat called with no active round');
        return;
      }
      const fromBeat = currentBeatRef.current;
      if (!isClientDrivenTransition(fromBeat, toBeat)) {
        console.warn('[Dojo WS] Refusing client-driven beat transition:', fromBeat, '->', toBeat);
        return;
      }
      // A step change must use the current connection, rather than wait in the
      // reconnect queue after the server may have already moved the round.
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !isAuthenticatedRef.current) {
        setIsSending(false);
        return;
      }
      setHasError(false);
      setError(null);
      setIsSending(true);
      const msg: WSAdvanceBeatMessage = {
        type: 'advance_beat',
        round_id: currentRoundIdRef.current,
        to_beat: toBeat,
      };
      try {
        sendRaw(msg);
      } catch (err) {
        setIsSending(false);
        setHasError(true);
        setError(err instanceof Error ? err.message : 'WebSocket connection error');
      }
    },
    [sendRaw]
  );

  const setExperienceLevel = useCallback(
    async (level: DojoExperienceLevel): Promise<void> => {
      setHasError(false);
      setError(null);
      // Optimistic: the picker is a gate, and the server echoes dojo_profile back.
      setProfile((prev) => (prev ? { ...prev, experience_level: level } : prev));
      const msg: WSSetExperienceLevelMessage = {
        type: 'set_experience_level',
        level,
      };
      sendRaw(msg);
    },
    [sendRaw]
  );

  const clearRound = useCallback(() => {
    setMessages([]);
    setCurrentRoundId(null);
    currentRoundIdRef.current = null;
    setCurrentUnit(null);
    setLearnerMarks([]);
    setCurrentScenario(null);
    setLastCompletion(null);
    setCurrentBeat('idle');
    currentBeatRef.current = 'idle';
    streamingTextRef.current = '';
    setStreamingText('');
    setHasError(false);
    setError(null);
    lastMessageRef.current = null;
    // Back leaves the round, not the learner's saved, round-scoped draft.
    clearPendingChatCommand('howie', resumableSurface);
    finishActiveTurn();
  }, [finishActiveTurn, resumableSurface]);

  const retryLastMessage = useCallback(async (): Promise<void> => {
    if (lastMessageRef.current) {
      setHasError(false);
      setError(null);
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last?.role === 'user' ? prev.slice(0, -1) : prev;
      });
      await sendMessage(lastMessageRef.current);
    }
  }, [sendMessage]);

  // ---------- Lifecycle ----------

  // Load profile from Supabase + connect on user change.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function loadProfile(): Promise<void> {
      try {
        const { data, error: dbError } = await supabase
          .from('howie_dojo_profiles')
          .select(
            'fifteen_cell_map, dialectic_positions, commitment_cadence, preferred_voice, rounds_completed, is_first_session, experience_level'
          )
          .eq('user_id', userId)
          .single();

        if (cancelled) return;

        if (dbError) {
          // Migration not yet applied or row not yet created — use defaults silently.
          if (dbError.code !== 'PGRST116' && dbError.code !== '42P01') {
            console.error('[Dojo WS] Profile load error:', dbError);
          }
          setProfile(DEFAULT_PROFILE);
          return;
        }

        if (data) {
          setProfile({
            fifteen_cell_map: (data.fifteen_cell_map as FifteenCellMap) ?? DEFAULT_FIFTEEN_CELL_MAP,
            dialectic_positions: (data.dialectic_positions as Record<string, DialecticPosition>) ?? {},
            commitment_cadence: data.commitment_cadence ?? null,
            preferred_voice: data.preferred_voice ?? null,
            rounds_completed: data.rounds_completed ?? 0,
            is_first_session: data.is_first_session ?? true,
            last_chiddush_id: null,
            experience_level: normalizeExperienceLevel(data.experience_level),
          });
        } else {
          setProfile(DEFAULT_PROFILE);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[Dojo WS] Profile load exception:', err);
          setProfile(DEFAULT_PROFILE);
        }
      }
    }

    void (async () => {
      await loadProfile();
      if (!cancelled) await loadUnitsCatalog();
    })();
    connect();

    return () => {
      cancelled = true;
      disconnect(true);
    };
  }, [userId, connect, disconnect, loadUnitsCatalog]);

  return {
    messages,
    currentBeat,
    currentRoundId,
    currentUnit,
    learnerMarks,
    setLearnerMarks,
    currentScenario,
    catalog,
    practiceProgress,
    profile,
    lastCompletion,
    streamingText,
    isLoading,
    isLoadingCatalog,
    isSending,
    isStreaming,
    canSteer,
    hasError,
    error,
    catalogError,
    connectionStatus,
    draft,
    setDraft,
    clearDraft,
    startRound,
    sendMessage,
    submitIntake,
    submitMarks,
    advanceBeat,
    setExperienceLevel,
    saveInsight,
    endRoundEarly,
    clearRound,
    retryLastMessage,
    refreshCatalog: loadUnitsCatalog,
  };
}

export default useHowieDojoWebSocket;
